import { useEffect, useRef, useState } from 'react'
import { useTexte } from '../../texte'

import { useQueryClient } from '@tanstack/react-query'

import {
  ensureSettings,
  ensureTrip,
  useDiscardMedia,
  useSendMessage,
  useTrip,
  useSettings,
  useUploadMedia,
  useWetterkarte,
  useTageskarte,
} from '../../hooks/queries'
import { merken, neuerSchluessel } from '../../lib/outbox'
import { Restzeichen } from '../../components/Restzeichen'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { api, ApiError, fehlertext, netzStand } from '../../api/client'
import type { Media, Settings, DayCardPreview } from '../../api/types'
import { bildgrenzen, canPreviewDirectly, isImage, prepareImage } from '../../lib/images'
import { dauerText, isVideo, uploadVideo } from '../../lib/video'
import { ausbeuteText, kannWandeln } from '../../lib/videowandeln'
import { groesse, medienwort } from '../../lib/format'
import { bildschirmtastaturVerdeckt } from '../../lib/tastatur'
import { useTastaturOffen } from '../../hooks/useTastatur'
import { notiere } from '../../lib/sendeprotokoll'
import {
  GifIcon,
  GruppeIcon,
  PhotoIcon,
  PlusIcon,
  PersonIcon,
  PfeilHochIcon,
  VideoIcon,
  WelleIcon,
  XIcon,
} from '../../components/Icons'
import { Anhangwahl, wegeZaehlen } from './Anhangwahl'
import { holeOrt } from '../../lib/ortung'
import { zeichneTagesroute } from './tagesroute'
import { Tagwahl } from './Tagwahl'
import { Wetterwahl } from './Wetterwahl'
import { STRICH_VORGABE } from '../map/strich'
import { useBuchquelle } from '../export/buchquelle'
import { Abstimmungsformular } from './Abstimmungsformular'
import { AUSZEICHNUNGEN } from '../../lib/auszeichnung'
import { alsText, auswahlIn, lese, schreibe, zeichneAus } from '../../lib/reichtext'
import {
  HOECHSTENS_MS,
  kannAufnehmen,
  starteAufnahme,
  ueberallAbspielbar,
  type LaufendeAufnahme,
} from '../../lib/sprachaufnahme'
import { Aufnahmeleiste } from './Aufnahmeleiste'
import { Gifwahl, useGifsAktiv } from './Gifwahl'
import type { Bezug } from './ThreadPage'

interface Props {
  tripId: number
  /**
   * Der Hinweis im leeren Feld — **von lang nach kurz**.
   *
   * Auf einem schmalen Telefon passt „Nachricht an alle Empfänger" nicht in
   * die Zeile und wurde abgeschnitten. Statt eines Auslassungszeichens
   * mitten im Wort steht hier eine Staffel: der Composer nimmt die längste
   * Fassung, die wirklich hineinpasst. Der erste Eintrag ist zugleich das,
   * was Vorlesehilfen bekommen — dort zählt die Breite nicht.
   */
  /**
   * Die Staffel der Platzhalter, von lang nach kurz.
   *
   * **`readonly`, seit der Wortlaut in `texte/de.ts` steht**: die Datei ist
   * `as const`, und was von dort kommt, ist unveränderlich. Gelesen wird es
   * hier ohnehin nur.
   */
  platzhalter: readonly string[]
  /**
   * Wer es sehen wird — daran hängt das Sinnbild im leeren Feld.
   *
   * **Abgeleitet, nicht aufgezählt:** ein Empfänger oder alle. Damit deckt
   * eine Angabe alle drei Fälle ab — privater Verlauf, Beitrag des Reisenden
   * an alle, Antwort eines Mitlesenden (die sieht nur der Reisende).
   *
   * Sie steht neben `platzhalter`, weil beides dieselbe Aussage macht: das
   * eine in Worten, das andere als Bild. Wer eines ändert, sieht das andere.
   */
  reichweite: 'einer' | 'alle'
  disabled?: boolean
  /**
   * Der geöffnete Antwortverlauf. Gesetzt heißt: was hier geschrieben wird,
   * geht an genau diesen Menschen — der Server macht daraus eine private
   * Nachricht.
   */
  withUser?: number
  /**
   * Was statt der Eingabezeile dasteht, wenn nicht geschrieben werden kann.
   *
   * Ohne Angabe: die abgeschlossene Reise, der ursprüngliche Fall. Der Grund
   * gehört an die Stelle, an der sonst das Schreibfeld wäre — ein Feld, das
   * nichts kann, verspricht etwas, das es nicht hält.
   */
  disabledHint?: React.ReactNode
  /**
   * Worauf sich die Nachricht bezieht — gesetzt an einer Sprechblase weiter
   * oben oder an einem Foto in der Großansicht.
   */
  bezug?: Bezug | null
  /** Den Bezug wieder loswerden: das ✕ am Zitat, und nach dem Senden. */
  onBezugWeg?: () => void
  /**
   * Etwas ist raus -- der Verlauf soll ans Ende springen.
   *
   * **Warum das jemand anders macht:** die Zeitleiste gehört der Seite, nicht
   * der Eingabezeile. Hier weiß nur diese Stelle, *wann* etwas hinausgeht --
   * und dort weiß nur die Seite, *wohin* gesprungen wird.
   */
  onGesendet?: () => void
}

/**
 * `offline` ist die neue Stufe: das Bild ist fertig gerechnet, aber es gibt
 * kein Netz. Es liegt dann als Blob bereit und geht mit der Nachricht in den
 * Ausgangskorb, statt als Fehler dazustehen.
 */
type Stufe = 'wartet' | 'rechnet' | 'lädt' | 'fertig' | 'offline' | 'fehler'

/**
 * Ein ausgewähltes Foto auf seinem Weg zum Server.
 *
 * Der Schlüssel ist ein eigener: die id vom Server gibt es erst am Ende des
 * Weges, die Kachel aber schon am Anfang.
 */
interface Attachment {
  key: number
  preview: string | null
  stufe: Stufe
  /** Anteil des Hochladens, 0 bis 1. */
  fortschritt: number
  /**
   * Was die Datei wiegt -- und zwar von Anfang an.
   *
   * Der iOS-Teilen-Dialog merkt sich die Größenauswahl nicht (Format und
   * Standort schon). Von derselben Aufnahme kommt deshalb mal 25 MB und mal
   * 300 MB, je nachdem ob man daran gedacht hat. Wer das erst erfährt, wenn
   * der Server ablehnt, hat schon gewartet.
   */
  bytes: number
  art: 'bild' | 'video' | 'ton'
  /** Nur bei Videos, sobald gelesen. */
  dauerMs?: number
  /** Die Grenze, an der diese Datei scheitern wird -- in MB, sonst 0. */
  ueberGrenze?: number
  /**
   * Das fertig verkleinerte Bild.
   *
   * Bleibt liegen, bis die Nachricht raus ist: ohne Netz wandert es in den
   * Ausgangskorb, und dort steht das Bild selbst, nicht seine id vom Server.
   */
  blob?: Blob
  /** Its thumbnail from `prepareImage()` -- goes into the outbox with it (1.3.0). */
  thumb?: Blob | null
  id?: number
  problem?: string
  /**
   * Ein Hinweis, der kein Fehler ist.
   *
   * Bisher gibt es genau einen: das Video konnte nicht verkleinert werden und
   * geht so hinaus, wie es aus der Kamera kam. Das gehört gesagt -- sonst
   * wundert sich jemand, warum ein Upload so lange dauert -- aber es ist
   * nichts fehlgeschlagen, und deshalb ist es nicht `problem`.
   */
  hinweis?: string
  /**
   * Was aus dem Video wurde: „184 MB → 27 MB · 1080p".
   *
   * Steht an der Kachel, weil dort die Frage entsteht — „ist das jetzt
   * kleiner, und wie klein?". Nach dem Absenden ist die Kachel weg; wer es
   * später nachsehen will, findet Größe und Maße am Video im Verlauf.
   */
  ausbeute?: string
}

let naechsterSchluessel = 1

/**
 * Das Vorschaubild zum Bezug -- aus den Daten des offenen Chats.
 *
 * Hier ausnahmsweise aus dem Zwischenspeicher und nicht vom Server: das Zitat
 * über der Eingabezeile sieht **nur der, der gerade schreibt**, und zwar zu
 * einer Nachricht, die vor ihm im selben Verlauf steht. In der Sprechblase
 * einer gesendeten Antwort ist es umgekehrt -- dort liefert der Server den
 * Auszug, denn dort lesen andere mit.
 */
function bezugBild(bezug: Bezug): string | null {
  const medium = bezugMedium(bezug)
  // **Eine Sprachnachricht hat kein Bild.** Ohne diese Zeile stünde ihre
  // Tondatei als Bildadresse im Zitat -- ein kaputtes Bildchen.
  if (medium === undefined || medium.kind === 'audio') return null
  return medium.poster_url ?? medium.url ?? null
}

/** Das gemeinte Medium — an einer Stelle gesucht, dreimal gebraucht. */
function bezugMedium(bezug: Bezug) {
  return bezug.mediaId === undefined
    ? undefined
    : bezug.message.media.find((m) => m.id === bezug.mediaId)
}



/**
 * Was das Sinnbild im leeren Feld an Breite kostet — 16 Punkte plus 6 Abstand.
 *
 * Als Zahl und nicht gemessen: sie steht in `app.css` an `.composer__leer`
 * genauso, und beides zu messen hieße, auf ein Bauteil zu warten, das es im
 * Augenblick der Messung noch gar nicht gibt.
 */
const SINNBILD_PLATZ = 22

/** Die Eingabezeile: wächst mit dem Text, Enter sendet, Umschalt+Enter bricht um. */
export function Composer({
  tripId,
  platzhalter,
  reichweite,
  disabled,
  withUser,
  disabledHint,
  bezug,
  onBezugWeg,
  onGesendet,
}: Props) {
  const t = useTexte()
  const [text, setText] = useState('')
  const [fotos, setFotos] = useState<Attachment[]>([])
  const grenzen = useTextgrenzen()
  const [problem, setProblem] = useState<string | null>(null)
  const [sendeWennFertig, setSendeWennFertig] = useState(false)
  const [gifOffen, setGifOffen] = useState(false)
  // Die laufende Aufnahme -- als Ref, weil sie kein Neuzeichnen auslöst, und
  // die verstrichene Zeit daneben als Zustand, weil sie eine.
  const aufnahme = useRef<LaufendeAufnahme | null>(null)
  const [nimmtAuf, setNimmtAuf] = useState(false)
  const [pausiert, setPausiert] = useState(false)
  const [seit, setSeit] = useState(0)
  /*
   * **Der `AudioContext` entsteht im Tipp und lebt danach weiter.**
   * Safari lässt ihn nur in einer Benutzergeste anlaufen, und nach dem
   * `await` auf `getUserMedia` ist die Geste vorbei. Einmal angelaufen, darf
   * er bleiben: ein zweiter Tipp findet ihn vor, und ein geschlossener wäre
   * beim zweiten Mal wieder ein Fall für die Geste.
   */
  const tonkontext = useRef<AudioContext | null>(null)
  const [tonHinweis, setTonHinweis] = useState<string | null>(null)
  const field = useRef<HTMLDivElement>(null)
  const picker = useRef<HTMLInputElement>(null)
  // Die Uploads gehen nacheinander, aufgereiht an dieser Kette. Das nächste
  // Bild wird schon verkleinert, während das vorige über die Leitung geht --
  // Rechnen und Senden überlappen, und beides ist auf einem Telefon der
  // Flaschenhals.
  const kette = useRef<Promise<void>>(Promise.resolve())

  const send = useSendMessage(tripId, withUser)
  const upload = useUploadMedia(tripId)
  const discard = useDiscardMedia()
  // Nur für die Uhr der Reise -- die Abfrage liegt im Zwischenspeicher.
  const reiseVersatz = useTrip(tripId).data?.tz_offset_min ?? null
  const client = useQueryClient()
  /**
   * **Der Reisende erzählt, die anderen antworten.**
   *
   * Fotos und Videos sind der Bericht: sie kosten Kontingent und stehen
   * dauerhaft in der Reise. Eine Antwort trägt Worte — und ein GIF, weil das
   * zum Ton gehört. Entschieden wird das am Server (die Rolle, nicht der
   * Client); hier steht nur, was gar nicht erst zur Auswahl kommt.
   *
   * **Für Mitlesende gibt es deshalb keine Dateiauswahl mehr.** Sie war der
   * Umweg: der iOS-Dialog bot dort „Fotos" und „Foto aufnehmen" an, obwohl
   * beides abgewiesen wurde, und wer wirklich ein GIF wollte, musste es sich
   * vorher irgendwo besorgen. GIFs kommen jetzt aus der Auswahl daneben.
   */
  /**
   * **Zwei Flaggen statt einer** (#59).
   *
   * Bis hierher hieß es „der Reisende darf Dateien, alle anderen nicht". Seit
   * #59 gehört ein Foto auch in eine Antwort; ein Video bleibt beim Bericht,
   * weil es Kontingent in einer anderen Größenordnung kostet und in Stücken
   * hereinkommt. Die Trennung steht deshalb hier und nicht an fünf Stellen
   * verteilt — und sie heißt, was sie meint.
   */
  const istReisender = useTrip(tripId).data?.role === 'sender'
  const reiseKarte = useTrip(tripId).data?.map_style
  const darfFotos = true
  // Ohne GIPHY_API_KEY steht der Knopf nicht da -- dann bleibt es bei Worten.
  const gifsAktiv = useGifsAktiv()
  /** Steht das Blatt mit „Foto oder Video / GIF suchen" offen? */
  const [anhangOffen, setAnhangOffen] = useState(false)
  /**
   * Wie viele Wege es überhaupt gibt — daran hängt, ob es den `+` gibt.
   *
   * An einer Stelle abgeleitet und nicht zweimal hingeschrieben: das Blatt
   * zeigt dieselben Zeilen, die hier gezählt werden.
   */
  /**
   * **Eine Abstimmung gehört in den Reise-Chat und nicht in einen
   * Zweiergespräch.** Sie ist ein Beitrag an alle; im Verlauf mit einem
   * Einzelnen wäre sie eine Abstimmung mit einem Menschen, und das ist eine
   * Frage. Deshalb `withUser === undefined` und nicht nur die Rolle.
   */
  const darfAbstimmung = istReisender && withUser === undefined
  const [abstimmungOffen, setAbstimmungOffen] = useState(false)
  /*
   * **The weather (0.31.0) under the same conditions as the poll** -- a post
   * to everyone, by the traveller -- and only if the server can ask Apple:
   * without the keys the entry is not there at all.
   */
  const einstellungen = useSettings()
  const darfWetter = darfAbstimmung && einstellungen.data?.services?.weather === true
  // A chosen place and hour (#83) needs the place search as well -- Apple Maps, not only WeatherKit.
  const darfWetterAnderswo = darfWetter && einstellungen.data?.services?.place === true
  const wetter = useWetterkarte(tripId)
  /** "Wo und wann?" is open (#83). */
  const [wetterwahlOffen, setWetterwahlOffen] = useState(false)
  /*
   * The day card (#79) under the same conditions as the poll -- a post to
   * everyone, by the traveller. Whether there is a route with a day before
   * today says the server when asked; without one the tap ends in a
   * sentence, not in silence.
   */
  const darfTag = darfAbstimmung
  const tageskarte = useTageskarte(tripId)
  const buchquelle = useBuchquelle(tripId)
  /** The days to choose from, while the question "Welcher Tag?" is open (0.41.0). */
  const [tagwahl, setTagwahl] = useState<DayCardPreview | null>(null)
  const [aktionStand, setAktionStand] = useState<string | null>(null)
  const wege = wegeZaehlen(darfFotos, gifsAktiv, darfAbstimmung, darfWetter, darfTag, darfWetterAnderswo)

  /**
   * Get the position, send the weather -- one tap, two steps, and each says
   * what it does. Without the net not at all: no silent failure.
   */
  async function wetterSchicken() {
    if (!netzStand()) {
      setAktionStand(t.chat.wetterOhneNetz)
      return
    }
    setAktionStand(t.chat.wetterOrtung)
    try {
      const ort = await holeOrt()
      setAktionStand(t.chat.wetterHolen)
      await wetter.mutateAsync({ lat: ort.lat, lon: ort.lon })
      setAktionStand(null)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }
  /** The weather at a chosen place and hour (#83): the sheet chose, the server asks Apple. */
  async function wetterAnderswoSchicken(wahl: { lat: number; lon: number; at: string }) {
    setWetterwahlOffen(false)
    if (!netzStand()) {
      setAktionStand(t.chat.wetterOhneNetz)
      return
    }
    setAktionStand(t.chat.wetterHolen)
    try {
      await wetter.mutateAsync(wahl)
      setAktionStand(null)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }
  /**
   * The day card: ask the server, draw the map, send -- three steps, each
   * says what it does. Without the net not at all.
   */
  async function tagFragen() {
    if (!netzStand()) {
      setAktionStand(t.chat.tagOhneNetz)
      return
    }
    setAktionStand(t.chat.tagAuswerten)
    try {
      // The days the route has, and the proposal -- the question comes
      // before any drawing, so that nothing is drawn for the wrong day.
      const vorschau = await api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`)
      setAktionStand(null)
      setTagwahl(vorschau)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }
  async function tagSchicken(wahl: { day: string; from?: string; to?: string }) {
    setTagwahl(null)
    setAktionStand(t.chat.tagAuswerten)
    // The day card's own stroke (#80; until then the map's, #50): the map
    // of the day is a picture in the chat, and a line that reads well on
    // the live map can be too thin there. The pins carry the colour of a
    // published point, as on the map.
    const strich = {
      farbe: reiseKarte?.daycard_route_color ?? STRICH_VORGABE.farbe,
      breite: reiseKarte?.daycard_route_width ?? STRICH_VORGABE.breite,
      nadel: reiseKarte?.pin_color ?? '#007aff',
    }
    try {
      await tageskarte.mutateAsync({
        ...wahl,
        zeichnen: (punkte, nadeln) => zeichneTagesroute(punkte, buchquelle, strich, nadeln),
        vorher: (stand) => setAktionStand(stand === 'zeichnen' ? t.chat.tagZeichnen : t.chat.tagSchicken),
      })
      setAktionStand(null)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }
  /**
   * Gibt es überhaupt etwas zu senden?
   *
   * **An einer Stelle abgeleitet.** Daran hängt, ob rechts im Feld das
   * Wellenform oder der Sendeknopf steht -- und dass es immer genau eines ist.
   */
  const etwasZuSenden = text.trim() !== '' || fotos.length > 0

  /**
   * Zu lang -- **an einer Stelle abgeleitet, wie oben.**
   *
   * **Gekürzt wird hier nichts.** Das Feld ist ein `contentEditable` und
   * ungesteuert mit Absicht (siehe den Doc-Block weiter unten): ein
   * zurückgeschriebener Wortlaut setzte den Cursor ans Ende, mitten im
   * Tippen. Ein `maxLength` gibt es an einem solchen Feld ohnehin nicht.
   *
   * Also bleibt der Text stehen, der Zähler sagt es, und der Sendeknopf ist
   * aus. Das ist keine stille Absage: es steht als Satz da, nicht als
   * `title`.
   *
   * Gezählt wird in Codepunkten (`[...text]`), weil der Server das auch tut
   * (`mb_strlen`). `text.length` zählte ein Emoji als zwei und sperrte damit
   * früher als nötig.
   */
  const zuLang = [...text.trim()].length > grenzen.message

  /**
   * Liegt eine Bildschirmtastatur über der Anwendung?
   *
   * Daran hängt der zweite Platz in der Zeile: der Knopf der Sprachnachricht
   * verliert dort seinen Tipp (siehe unten am Zweig), also steht bei offener
   * Tastatur der Sendeknopf an seiner Stelle.
   */
  const tastaturOffen = useTastaturOffen()

  /**
   * Ob diese Datei über der Grenze liegt -- und über welcher.
   *
   * Die Grenze gilt ohnehin auf dem Server; hier steht sie nur, damit man es
   * vor dem Warten sieht statt danach. Ohne bekannte Werte wird nichts
   * behauptet.
   */
  function ueberGrenze(
    bytes: number,
    art: 'bild' | 'video' | 'ton',
    werte: Settings | null,
  ): number | undefined {
    if (!werte) return undefined
    const grenze = art === 'video' ? werte.video_max_mb : werte.image_max_mb
    return bytes > grenze * 1024 * 1024 ? grenze : undefined
  }

  // „Beschäftigt" heißt: da läuft noch etwas, warte damit. Ein Bild, das auf
  // Netz wartet, läuft gerade **nicht** -- es wäre sonst für immer beschäftigt
  // und der Beitrag ginge nie in den Korb.
  const beschaeftigt = fotos.some((foto) => unterwegs(foto.stufe))
  // Abgeleitet und nicht gemerkt, und das ist die Korrektur eines Fehlers:
  // vorher stand hier ein eigener Zustand, der beim Auswählen eines Videos auf
  // `true` ging und **nirgends** zurück. Der Hinweis blieb damit stehen, auch
  // nachdem die Nachricht längst raus war -- bis jemand den Chat verließ und
  // neu betrat. Was aus den Anhängen ablesbar ist, gehört nicht daneben noch
  // einmal aufgeschrieben.
  const fertige = fotos.filter((foto) => foto.stufe === 'fertig')

  /**
   * Ein `contenteditable` wächst von selbst — gedeckelt wird in der CSS
   * (`max-height`). Geblieben ist die Funktion für den Ausgangszustand: nach
   * dem Leeren soll die Zeile wieder einzeilig dastehen.
   */
  function grow() {
    field.current?.scrollTo({ top: field.current.scrollHeight })
  }

  function aendere(key: number, teil: Partial<Attachment>) {
    setFotos((alt) => alt.map((foto) => (foto.key === key ? { ...foto, ...teil } : foto)))
  }

  /**
   * Fotos annehmen -- und zwar sofort.
   *
   * Für jede Datei steht die Kachel da, bevor irgendetwas gerechnet ist. Das
   * ist der ganze Punkt: der Auswahldialog gibt die Bilder ab und man ist
   * zurück im Chat, statt vor einem stehengebliebenen Bildschirm zu warten,
   * bis fünf iPhone-Fotos verkleinert sind.
   */
  async function add(files: FileList | null) {
    if (!files || files.length === 0) return
    setProblem(null)

    /*
     * **Die Liste wird sofort festgehalten, vor jedem `await`.**
     *
     * Eine `FileList` ist keine Kopie, sondern der Blick auf das Feld — und
     * der Aufrufer setzt es unmittelbar nach dem Aufruf zurück (`value = ''`),
     * damit dieselbe Datei ein zweites Mal auslöst. Solange hier oben nichts
     * gewartet wurde, ging das gut; mit dem ersten `await` war sie leer, wenn
     * die Arbeit begann, und es geschah schlicht gar nichts.
     *
     * Beim Umbau für #59 ist genau das passiert, und der Prüfstand hat es
     * gefunden: der Sendeknopf des Mitlesenden erschien nie.
     */
    const gewaehlt = Array.from(files)

    /*
     * **Erst die Rolle, dann alles Weitere** (#59).
     *
     * Sie steht in `useTrip`, und beim ersten Öffnen eines Chats ist die
     * Abfrage noch unterwegs — wer in diesem Augenblick ein Foto auswählt,
     * gälte für einen Moment als Mitlesender. Daran hängt seit #59 die
     * Kantenlänge, mit der verkleinert wird, **und** ob ein Video überhaupt
     * durchdarf. Der Prüfstand hat genau das gemessen: das Foto des Reisenden
     * kam mit 800 statt 2000 Punkten an und sah dabei aus wie eines, das der
     * Einstellung folgt.
     *
     * Dieselbe Falle wie bei den Einstellungen eine Zeile weiter unten, und
     * dieselbe Antwort: gewartet wird, und zwar nur beim ersten Mal.
     */
    const [werte, reise] = await Promise.all([
      ensureSettings(client),
      ensureTrip(client, tripId),
    ])
    const alsReisender = reise === null ? istReisender : reise.role === 'sender'

    /*
     * **Ein Video eines Mitlesenden wird hier abgewiesen, nicht verschluckt.**
     * `accept` ist keine Sperre — auf iOS steht neben „Fotos" weiter „Alle
     * Dateien", und was dort ausgewählt wird, käme sonst bis zum Server und
     * würde erst dort abgelehnt: nach dem Umrechnen, nach dem Warten.
     *
     * Und es wird **gesagt**, nicht stillschweigend weggelassen. Etwas
     * auswählen und es passiert nichts — genau das soll es hier nie geben.
     */
    const videos = gewaehlt.filter((datei) => isVideo(datei))
    if (!alsReisender && videos.length > 0) {
      setProblem(
        videos.length === gewaehlt.length
          ? t.chat.nurVideosAbgewiesen
          : t.chat.videosAbgewiesen(videos.length),
      )
    }

    const aufgaben = gewaehlt
      .filter((datei) => alsReisender || !isVideo(datei))
      .map((file) => ({
        file,
        key: naechsterSchluessel++,
      }))
    if (aufgaben.length === 0) return

    setFotos((alt) => [
      ...alt,
      ...aufgaben.map(({ file, key }) => ({
        key,
        preview: canPreviewDirectly(file) ? URL.createObjectURL(file) : null,
        stufe: 'wartet' as Stufe,
        fortschritt: 0,
        bytes: file.size,
        art: (isVideo(file) ? 'video' : 'bild') as 'bild' | 'video',
      })),
    ])

    // **Die Grenze sofort -- aber nur, wenn sie schon feststeht.**
    //
    // Kann dieser Browser umrechnen, entscheidet erst das Ergebnis, wie gross
    // die Datei ist; ein rotes „über 200 MB", das nach einer Minute wieder
    // verschwindet, wäre eine Warnung, die sich selbst zurücknimmt. Kann er
    // es nicht, geht das Original hinaus, und dann stimmt sie vom ersten
    // Augenblick an -- und wer das erst erfährt, wenn der Server ablehnt, hat
    // schon gewartet.
    for (const { file, key } of aufgaben) {
      if (isVideo(file) && !kannWandeln()) {
        aendere(key, { ueberGrenze: ueberGrenze(file.size, 'video', werte) })
      }
    }

    // Hier kommt nur noch an, was der Reisende ausgewaehlt hat: die
    // Dateiauswahl steht nur ihm offen. Die Sperren gegen fremde Fotos und
    // Videos stehen weiterhin im Server -- dort, wo sie hingehoeren.
    for (const { file, key } of aufgaben) {
      // Ein Video geht einen eigenen Weg: **erst verkleinert** (#4), dann in
      // Stücken hochgeladen. Das Standbild entsteht dabei.
      //
      // Hier stand, ein Video werde nicht verkleinert, „der Browser könnte
      // es, aber es dauerte Minuten und macht das Telefon heiß". Das galt für
      // den Weg über ein `<video>`-Element, das dafür abspielen muss. Über
      // den Demuxer läuft es in Lesegeschwindigkeit.
      if (isVideo(file)) {
        // **Standbild und Dauer macht `uploadVideo` selbst** (#4). Hier
        // standen zwei eigene `<video>`-Elemente: eines fuer das Standbild,
        // eines nur fuer die Dauer -- und beide liefen los, waehrend der
        // Umrechner ebenfalls einen Dekodierer wollte. iOS gibt nur wenige
        // her. Die Dauer steht ohnehin im Befund, den `onErgebnis` bringt.
        kette.current = kette.current.then(async () => {
          try {
            aendere(key, { stufe: 'rechnet', fortschritt: 0 })
            const medium = await uploadVideo(
              tripId,
              file,
              (anteil) => aendere(key, { stufe: 'lädt', fortschritt: anteil }),
              {
                onVorschau: (bild) => aendere(key, { preview: URL.createObjectURL(bild) }),
                ziel: werte
                  ? {
                      maxEdge: werte.video_max_edge,
                      maxFps: werte.video_max_fps,
                      // Nur die beiden erlaubten Werte -- was der Server
                      // durchlaesst, ist geprueft, aber der Typ hier ist
                      // enger als `string`.
                      latenz: werte.video_latency === 'realtime' ? 'realtime' : 'quality',
                    }
                  : undefined,
                onRechnet: (anteil) => aendere(key, { stufe: 'rechnet', fortschritt: anteil }),
                // Keine stille Absage: wenn das Gerät nicht umrechnen kann,
                // steht der Grund an der Kachel -- und hochgeladen wird
                // nichts. Der Wurf kommt aus `uploadVideo` und landet im
                // `catch` weiter unten; **dieser** Rückruf ist vorher dran,
                // damit die Zahlen neben der Meldung stehen (#41).
                // **Ein Rückruf, aus dem alles folgt.** Grösse, Grenze,
                // Ausbeute und Hinweis kommen aus demselben Befund -- vorher
                // waren es zwei Rückrufe, und die Grenze wurde nur in einem
                // davon nachgezogen.
                onErgebnis: (befund) =>
                  aendere(key, {
                    // Die wahre Zahl: hier stand die Grösse der **ausgewählten**
                    // Datei, und nach dem Verkleinern war das die Grösse von
                    // etwas, das gar nicht hochgeht.
                    bytes: befund.nachher,
                    // Und die Grenze gilt für das, was hochgeht -- gerechnet
                    // mit derselben Funktion wie beim Auswählen.
                    ueberGrenze: ueberGrenze(befund.nachher, 'video', werte),
                    ausbeute: ausbeuteText(befund) ?? undefined,
                    // Die Dauer kennt der Zerleger -- dafuer braucht es kein
                    // eigenes `<video>`-Element mehr.
                    dauerMs: befund.dauerMs > 0 ? befund.dauerMs : undefined,
                    hinweis: befund.grund === '' ? undefined : befund.grund,
                  }),
              },
            )
            aendere(key, { stufe: 'fertig', fortschritt: 1, id: medium.id })
          } catch (fehler) {
            aendere(key, {
              stufe: 'fehler',
              problem: fehler instanceof Error ? fehler.message : t.chat.videoNichtDurch,
            })
          }
        })
        continue
      }

      if (!isImage(file)) {
        aendere(key, { stufe: 'fehler', problem: t.chat.wederBildNochVideo })
        continue
      }

      try {
        aendere(key, { stufe: 'rechnet' })
        const prepared = await prepareImage(file, {
          // **Welches Paar gilt, entscheidet `bildgrenzen()` und nicht diese
          // Zeile** (#59): die Frage gehört einmal beantwortet, nicht an jedem
          // Aufrufer.
          ...bildgrenzen(werte, alsReisender),
          // **Auch hier die Uhr der Reise**, obwohl es „nur" ein Chatfoto ist:
          // die Aufnahmezeit entscheidet später mit, welchem Aufenthalt das
          // Bild zugeordnet wird. Eine Stunde daneben, und es fällt aus dem
          // Fenster.
          reiseVersatz,
        })
        // Ab hier zählt das verkleinerte Bild, nicht die Datei aus der
        // Auswahl: das ist die Zahl, die über die Leitung geht.
        aendere(key, {
          bytes: prepared.blob.size,
          ueberGrenze: ueberGrenze(prepared.blob.size, 'bild', werte),
          // Das Bild bleibt hier liegen -- ohne Netz ist es das, was in den
          // Ausgangskorb wandert. Das Vorschaubild mit ihm (1.3.0).
          blob: prepared.blob,
          thumb: prepared.thumb,
        })

        // Ein Bild ohne eigene Vorschau (HEIC) bekommt sie jetzt.
        setFotos((alt) =>
          alt.map((foto) =>
            foto.key === key && foto.preview === null
              ? { ...foto, preview: URL.createObjectURL(prepared.blob) }
              : foto,
          ),
        )

        kette.current = kette.current.then(async () => {
          // Ohne Netz gar nicht erst anfangen: das Bild ist fertig und wartet
          // mit der Nachricht auf später. Gefragt wird `netzStand()` und nicht
          // `navigator.onLine` -- Letzteres sagt „online", sobald irgendein
          // WLAN da ist, auch wenn nichts hindurchgeht.
          if (!netzStand()) {
            aendere(key, { stufe: 'offline' })
            return
          }

          try {
            aendere(key, { stufe: 'lädt', fortschritt: 0 })
            const media = await upload.mutateAsync({
              blob: prepared.blob,
              // Aus dem Original gelesen, bevor verkleinert wurde -- der
              // Server legt Ort und Zeit an der Datei ab.
              aufnahme: prepared.aufnahme,
              // Das Vorschaubild fuer die Kachel (#57) -- es entsteht in
              // `prepareImage()` aus derselben Bitmap.
              thumb: prepared.thumb,
              onProgress: (anteil) => aendere(key, { fortschritt: anteil }),
            })
            aendere(key, { stufe: 'fertig', fortschritt: 1, id: media.id })
          } catch (fehler) {
            // Am Netz gescheitert heißt: später. Alles andere ist ein Fehler,
            // den man sehen muss -- ein Bild über der Grenze etwa geht auch
            // beim zehnten Versuch nicht durch.
            if (fehler instanceof ApiError && fehler.offline) {
              aendere(key, { stufe: 'offline' })
              return
            }
            // Ein gescheitertes Bild hält die anderen nicht auf.
            aendere(key, {
              stufe: 'fehler',
              problem: fehler instanceof Error ? fehler.message : t.chat.bildNichtDurch,
            })
          }
        })
      } catch (fehler) {
        aendere(key, {
          stufe: 'fehler',
          problem: fehler instanceof Error ? fehler.message : t.chat.bildNichtGeoeffnet,
        })
      }
    }
  }

  /**
   * Was gerade angeheftet ist — für das Aufräumen beim Verlassen.
   *
   * Der Effekt darunter läuft genau einmal und sähe sonst für immer die leere
   * Liste vom ersten Zeichnen.
   */
  const angeheftet = useRef<Attachment[]>([])
  angeheftet.current = fotos

  /**
   * Den Chat verlassen, ohne zu senden, räumt die Anhänge weg.
   *
   * Ein Foto geht sofort hinauf, die Nachricht erst beim Senden — wer den Chat
   * dazwischen verlässt, ließ bisher eine Zeile ohne Nachricht und ohne
   * Highlight zurück. Sie fällt niemandem auf, bis sie in „Fotos aus dieser
   * Reise" wieder auftaucht.
   *
   * Nach dem Senden steht hier nichts mehr: `aufraeumen()` leert die Liste,
   * und die ids gehören dann der Nachricht.
   *
   * Gelöscht wird hier **unmittelbar** und nicht über die Mutation: die hängt
   * an einem Bauteil, das gerade verschwindet. Scheitert es (kein Netz), bleibt
   * es beim Kehrbesen nach einem Tag — abreisen darf man trotzdem.
   */
  useEffect(() => {
    return () => {
      for (const foto of angeheftet.current) {
        if (foto.preview) URL.revokeObjectURL(foto.preview)
        if (foto.id !== undefined) void api.delete<void>(`/api/media/${foto.id}`).catch(() => {})
      }
    }
  }, [])

  /**
   * Ein GIF, das der Server schon geholt hat.
   *
   * Es hat bereits eine id und liegt bereits in der Reise -- es ist damit an
   * derselben Stelle wie ein fertig hochgeladenes Bild und geht denselben Weg
   * weiter. Ohne `blob`: in den Ausgangskorb gehört es nicht, denn geholt
   * wurde es ohnehin nur, weil gerade Netz da war.
   */
  function nimmGif(media: Media) {
    setProblem(null)
    setFotos((alt) => [
      ...alt,
      {
        key: naechsterSchluessel++,
        preview: media.url,
        stufe: 'fertig' as Stufe,
        fortschritt: 1,
        bytes: media.bytes,
        art: 'bild' as const,
        id: media.id,
      },
    ])
  }

  function remove(item: Attachment) {
    setFotos((alt) => alt.filter((foto) => foto.key !== item.key))
    if (item.preview) URL.revokeObjectURL(item.preview)
    // Auf dem Server auch wegräumen, sonst zählt es gegen das Kontingent,
    // bis es von selbst verfällt.
    if (item.id !== undefined) discard.mutate(item.id)
  }

  /** Nach dem Absenden: Feld leer, Kacheln weg, Vorschauen freigegeben. */
  function aufraeumen() {
    setText('')
    // Das Feld ist ungesteuert -- geleert wird es hier, ausdrücklich.
    if (field.current !== null) schreibe(field.current, '')
    fotos.forEach((foto) => foto.preview && URL.revokeObjectURL(foto.preview))
    setFotos([])
    setProblem(null)
    // Der Bezug gilt für diese eine Nachricht. Bliebe er stehen, hinge die
    // nächste ungefragt am selben Beitrag.
    onBezugWeg?.()
    requestAnimationFrame(grow)
  }

  /**
   * In den Ausgangskorb legen, statt zu verlieren.
   *
   * Mitgegeben werden die **Bilder**, nicht ihre ids -- auch die schon
   * hochgeladenen. Ein Foto, das vor dem Netzabbruch durchkam, hat zwar eine
   * id, aber die räumt der Server nach einem Tag weg; ein Eintrag, der morgen
   * früh darauf zeigt, wäre kaputt. Lieber einmal doppelt hochladen.
   */
  function inDenKorb(body: string, clientKey: string) {
    // Foto und Vorschaubild bleiben paarweise: derselbe Filter für beide Listen.
    const mitBild = fotos.filter((foto): foto is Attachment & { blob: Blob } => foto.blob !== undefined)
    void merken({
      tripId,
      withUser,
      body,
      clientKey,
      // Der Bezug muss den Korb überstehen: ohne ihn käme die Antwort später
      // ohne ihren Anfang heraus, und niemand wüsste mehr, worauf sie ging.
      replyToId: bezug?.message.id,
      replyToMediaId: bezug?.mediaId,
      bilder: mitBild.map((foto) => foto.blob),
      vorschauen: mitBild.map((foto) => foto.thumb ?? null),
    }).then(aufraeumen)
  }

  /**
   * Eine Auszeichnung auf die Auswahl legen — oder wieder wegnehmen.
   *
   * **Die Auswahl bleibt stehen.** Deshalb hängt der Knopf an `mousedown`
   * mit `preventDefault` (siehe unten): ein Knopf, der den Fokus nimmt,
   * löscht die Markierung, und danach gibt es nichts mehr auszuzeichnen.
   */
  function lege(art: (typeof AUSZEICHNUNGEN)[number]['art']) {
    const feld = field.current
    if (feld === null) return
    zeichneAus(art)
    setText(lese(feld))
    grow()
  }

  /**
   * **Die Leiste steht nur da, solange etwas markiert ist.**
   *
   * Gewünscht war sie „nicht als feste Symbole, sondern als Unterpunkt im
   * Pop-Up-Menü". In Apples eigenes Menü kommt eine Webseite nicht hinein —
   * das kann nur eine native App. Das Nächstliegende ist dies: sie erscheint
   * beim Markieren und verschwindet wieder. Angeschlagen wird sie über
   * `selectionchange`, denn ein `onSelect` gibt es an einem
   * `contenteditable` nicht.
   */
  /**
   * Welche Fassung des Hinweises passt.
   *
   * **Gemessen und nicht geraten:** die Breite eines Textes hängt an der
   * Schrift, und die hängt seit der Schriftgrößen-Einstellung am Gerät.
   * `measureText` fragt genau die Schrift, die im Feld steht — eine
   * Faustzahl („unter 400 Punkten die kurze") ginge bei der nächsten Stufe
   * daneben.
   */
  const [stufe, setStufe] = useState(0)
  useEffect(() => {
    const feld = field.current
    if (feld === null) return

    const messen = () => {
      const stil = getComputedStyle(feld)
      // **Das Sinnbild steht vor dem Text und nimmt Platz.** Es hier
      // abzuziehen ist die einzige Stelle, an der die Staffel davon weiß --
      // sonst wählte sie eine Fassung, die dann doch nicht hineinpasst.
      const platz = feld.clientWidth
        - Number.parseFloat(stil.paddingLeft)
        - Number.parseFloat(stil.paddingRight)
        - SINNBILD_PLATZ
      if (platz <= 0) return
      const stift = document.createElement('canvas').getContext('2d')
      if (stift === null) return
      stift.font = `${stil.fontStyle} ${stil.fontWeight} ${stil.fontSize} ${stil.fontFamily}`
      const passt = platzhalter.findIndex((wort) => stift.measureText(wort).width <= platz)
      setStufe(passt === -1 ? platzhalter.length - 1 : passt)
    }

    messen()
    const beobachter = new ResizeObserver(messen)
    beobachter.observe(feld)
    return () => beobachter.disconnect()
  }, [platzhalter])

  const [hatAuswahl, setHatAuswahl] = useState(false)
  useEffect(() => {
    const schauen = () => setHatAuswahl(auswahlIn(field.current))
    document.addEventListener('selectionchange', schauen)
    return () => document.removeEventListener('selectionchange', schauen)
  }, [])

  /**
   * Aufnehmen anfangen — und den Zähler mitlaufen lassen.
   *
   * **Keine stille Absage:** verweigert das Gerät das Mikrofon, steht der
   * Grund als Text da. Ein Knopf, auf den nichts folgt, ist das Schlimmste.
   */
  async function nimmAuf() {
    setTonHinweis(null)
    /*
     * **Noch in der Geste, vor jedem `await`.** Danach ist der Tipp vorbei,
     * und Safari lässt einen `AudioContext` dann nicht mehr anlaufen — die
     * Wellenform bliebe flach. Scheitert er, läuft alles Übrige weiter.
     */
    if (tonkontext.current === null) {
      try {
        tonkontext.current = new AudioContext()
      } catch {
        tonkontext.current = null
      }
    }
    void tonkontext.current?.resume()

    try {
      aufnahme.current = await starteAufnahme(tonkontext.current ?? undefined)
      setNimmtAuf(true)
      setPausiert(false)
      setSeit(0)
    } catch {
      setTonHinweis(
        t.chat.mikrofonGesperrt,
      )
    }
  }

  /** Anhalten und weitermachen — der Zähler steht dabei still. */
  function pausiereAufnahme() {
    aufnahme.current?.pausieren()
    setPausiert(true)
  }

  function weiterAufnehmen() {
    aufnahme.current?.weiter()
    setPausiert(false)
  }

  /**
   * Aufnahme beenden und als Anhang aufnehmen — wie ein fertiges Bild.
   *
   * **`undSenden` ist der Kern der Meldung** (#63): „Zum Beenden + Senden dann
   * direkt den Senden-Knopf drücken." Gesendet wird nicht hier, sondern über
   * die Vormerkung, die es für Fotos längst gibt — die Aufnahme muss erst
   * hochgeladen sein, und genau darauf wartet `sendeWennFertig`. Zwei Wege
   * zum Senden wären zwei Wahrheiten.
   */
  async function beendeAufnahme(undSenden = false) {
    const laufend = aufnahme.current
    if (!laufend) return
    aufnahme.current = null
    setNimmtAuf(false)
    setPausiert(false)

    const roh = await laufend.stoppen()
    if (roh.blob.size === 0) return

    // **Die Kachel steht da, bevor gerechnet wird** -- dieselbe Reihenfolge
    // wie beim Verkleinern eines Fotos. Wo MP4 herauskam, ist `rechnet` nur
    // ein Wimpernschlag; wo umgewandelt wird, ist es die Auskunft, dass
    // etwas läuft und die Aufnahme nicht verloren ist.
    const key = naechsterSchluessel++
    setFotos((alt) => [
      ...alt,
      {
        key,
        preview: null,
        stufe: 'rechnet' as Stufe,
        fortschritt: 0,
        bytes: roh.blob.size,
        art: 'ton' as const,
        dauerMs: roh.dauerMs,
      },
    ])

    const fertig = await ueberallAbspielbar(roh)
    if (!fertig.ueberall) {
      // Nur noch die letzte Rückfalllinie: das Umwandeln selbst ist
      // gescheitert. Hochgeladen wird trotzdem.
      setTonHinweis(
        t.chat.formatStumm,
      )
    }
    aendere(key, { stufe: 'lädt', bytes: fertig.blob.size })

    try {
      const medium = await api.upload<Media>(
        `/api/trips/${tripId}/media/voice`,
        fertig.blob,
        (anteil) => aendere(key, { fortschritt: anteil }),
        { 'x-dauer-ms': String(Math.round(fertig.dauerMs)) },
      )
      aendere(key, { stufe: 'fertig', fortschritt: 1, id: medium.id })
      /* **Erst jetzt vormerken, nicht beim Druck.** Beim Druck steht die
         Kachel noch nicht da; `submit()` fände nichts zu senden und nähme die
         Vormerkung gleich wieder zurück. Hier ist die Aufnahme oben und die
         Vormerkung greift im nächsten Zeichnen. */
      if (undSenden) setSendeWennFertig(true)
    } catch (fehler) {
      aendere(key, {
        stufe: 'fehler',
        problem: fehler instanceof Error ? fehler.message : t.chat.aufnahmeNichtDurch,
      })
    }
  }

  function brichAufnahmeAb() {
    aufnahme.current?.abbrechen()
    aufnahme.current = null
    setNimmtAuf(false)
    setPausiert(false)
  }

  /*
   * Der Zähler, und die Decke: bei zwei Minuten ist Schluss, von selbst.
   *
   * **Gefragt statt gezählt.** Hier wurden 250 Millisekunden je Schlag
   * addiert; mit einer Pause zählte das weiter, und am Ende stünde an der
   * Sprechblase eine Länge, die das Abspielen nie erreicht. Die Aufnahme
   * selbst weiß, wie lange sie schon läuft (`dauerJetzt()`), und sie zählt
   * die Pausen nicht mit. Ein Zeitgeber, der zu spät kommt, verschiebt die
   * Zahl damit auch nicht mehr.
   *
   * **Und was von selbst endet, geht nicht von selbst hinaus.** Die Decke
   * beendet die Aufnahme und legt sie als Anhang hin — gesendet wird sie erst
   * auf Druck. Etwas ungefragt zu verschicken, wäre das Gegenteil dessen, was
   * der Knopf verspricht.
   */
  useEffect(() => {
    if (!nimmtAuf) return
    const uhr = setInterval(() => {
      const jetzt = aufnahme.current?.dauerJetzt() ?? 0
      setSeit(jetzt)
      // The ceiling is a setting since 1.9.0; the constant only bridges the
      // moment before the settings arrive.
      const decke = (einstellungen.data?.settings.voice_max_seconds ?? HOECHSTENS_MS / 1000) * 1000
      if (jetzt >= decke) void beendeAufnahme(false)
    }, 100)
    return () => clearInterval(uhr)
    // `beendeAufnahme` hängt an nichts, was sich währenddessen ändert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nimmtAuf])

  function submit() {
    const body = text.trim()
    // **Jeder stille Ausgang wird aufgeschrieben** (#28). Ohne das ist „geht
    // manchmal, manchmal nicht" nicht zu beantworten.
    if (!body && fotos.length === 0) return notiere('submit: nichts da')
    // **Auch hier und nicht nur am Knopf.** Die Eingabetaste sendet ebenfalls,
    // und eine Regel, die nur an einer der beiden Türen steht, ist keine.
    if (zuLang) return notiere('submit: zu lang')
    if (send.isPending) return notiere('submit: läuft noch')

    // Noch nicht fertig: nicht abweisen, sondern vormerken. Wer unterwegs
    // Fotos schickt, soll das Telefon weglegen können.
    if (beschaeftigt) {
      setSendeWennFertig(true)
      return notiere('submit: vorgemerkt, Foto noch nicht fertig')
    }

    setSendeWennFertig(false)

    // Ein Schlüssel für diese eine Nachricht, gleich hier vergeben: geht der
    // Versuch am Netz kaputt, wandert **derselbe** in den Korb. Sonst stünde
    // der Beitrag doppelt da, wenn der Server ihn längst hatte und nur die
    // Antwort verlorenging.
    const clientKey = neuerSchluessel()

    // Ohne Netz -- oder mit einem Bild, das deshalb liegengeblieben ist --
    // wird gar nicht erst versucht.
    if (!netzStand() || fotos.some((foto) => foto.stufe === 'offline')) {
      // Ein Video wartet nicht im Korb: zweihundert Megabyte gehören nicht in
      // IndexedDB, und stillschweigend weglassen wäre schlimmer als absagen.
      if (fotos.some((foto) => foto.art === 'video')) {
        setProblem(
          t.chat.videoBrauchtNetz,
        )
        return notiere('submit: Video ohne Netz')
      }
      inDenKorb(body, clientKey)
      onGesendet?.()
      return notiere('submit: in den Ausgangskorb')
    }

    if (!body && fertige.length === 0) return notiere('submit: nichts Fertiges')

    // **Jetzt springen und nicht erst bei der Antwort.** Wer sendet, will
    // sehen, dass es unten steht -- und bis der Server antwortet, vergeht auf
    // einem Telefon eine spürbare Weile. Kommt die Nachricht später an, steht
    // der Verlauf ohnehin schon am Ende und bleibt dort.
    onGesendet?.()
    notiere('submit: geht raus')

    send.mutate(
      {
        body,
        mediaIds: fertige.map((foto) => foto.id!),
        clientKey,
        replyToId: bezug?.message.id,
        replyToMediaId: bezug?.mediaId,
      },
      {
        onSuccess: aufraeumen,
        onError: (fehler) => {
          // Am Netz gescheitert: nicht verloren, sondern verschoben. Eine
          // Absage des Servers dagegen bleibt eine Absage -- sie stünde im
          // Korb bis in alle Ewigkeit.
          if (fehler instanceof ApiError && fehler.offline) inDenKorb(body, clientKey)
        },
      },
    )
  }

  /**
   * **Und derselbe Griff ein zweites Mal, tiefer.**
   *
   * Aus dem Betrieb (#28): „Pfeiltaste in der Nachricht schließt nur die
   * Bildschirmtastatur; zum Senden muss man nochmal drücken." Auf iOS kommt
   * der Druck auf die Eingabetaste in einem `contenteditable` nicht
   * verlässlich als `keydown` an -- er wird von der Autokorrektur oder vom
   * Schließen der Tastatur verbraucht. `beforeinput` kommt danach und sagt,
   * **was der Benutzer wollte**, statt welche Taste unterwegs war:
   * `insertParagraph` ist die Eingabetaste, `insertLineBreak`
   * Umschalt+Eingabe. Die Unterscheidung, die oben `shiftKey` macht, steht
   * hier also schon im Ereignis.
   *
   * **Ein echter Zuhörer und kein `onBeforeInput`.** React 19 reicht das
   * Ereignis zwar durch, aber `inputType` steht nur am nativen darunter; der
   * erste Anlauf las es am synthetischen und tat deshalb gar nichts -- der
   * Prüflauf hat es mit „0 Anfragen" gefangen.
   *
   * **Zweimal gesendet wird nichts:** greift `keydown`, hält sein
   * `preventDefault` das `beforeinput` auf; kommt kein `keydown`, ist dies
   * der einzige Weg. Der Ref auf `submit` hält den Zuhörer frisch, ohne ihn
   * bei jedem Zeichen neu zu hängen.
   */
  /**
   * Was die Eingabetaste tut -- an einer Stelle für beide Wege.
   *
   * **Die Regel kommt aus dem Betrieb (#28)** und ist die von iMessage und
   * WhatsApp: mit einer angeschlossenen Tastatur sendet sie, auf der
   * Bildschirmtastatur macht sie einen Absatz. Woran die beiden Fälle
   * auseinandergehalten werden, steht in `bildschirmtastaturVerdeckt()`.
   *
   * **Und im leeren Feld tut sie gar nichts.** Aus derselben Meldung: „ENTER
   * funktioniert, wenn noch keine Eingabe erfolgt ist -- dann werden
   * Leerzeilen erzeugt." Ein Absatz vor dem ersten Zeichen ist kein Absatz,
   * sondern eine leere Zeile, die niemand wollte.
   */
  function eingabetaste(event: { preventDefault: () => void }) {
    if (bildschirmtastaturVerdeckt()) {
      // Der Absatz ist die Vorgabe des Browsers -- außer im leeren Feld.
      if (text.trim() === '') event.preventDefault()
      return
    }
    event.preventDefault()
    submit()
  }

  const taste = useRef(eingabetaste)
  taste.current = eingabetaste

  useEffect(() => {
    const el = field.current
    if (el === null) return
    const aufAbsatz = (event: Event) => {
      if ((event as InputEvent).inputType !== 'insertParagraph') return
      taste.current(event)
    }
    el.addEventListener('beforeinput', aufAbsatz)
    return () => el.removeEventListener('beforeinput', aufAbsatz)
  }, [])

  // Der vorgemerkte Versand, sobald das letzte Bild durch ist. Ein
  // gescheitertes Bild nimmt die Vormerkung zurück -- sonst ginge der Beitrag
  // ohne das Foto raus, das man eigentlich zeigen wollte.
  useEffect(() => {
    if (!sendeWennFertig || beschaeftigt) return
    if (fotos.some((foto) => foto.stufe === 'fehler')) {
      setSendeWennFertig(false)
      setProblem(t.chat.fotoKamNichtDurch)
      return
    }
    setSendeWennFertig(false)
    submit()
    // submit hängt an Text und Fotos; die stehen zu diesem Zeitpunkt fest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendeWennFertig, beschaeftigt])

  if (disabled) {
    return (
      <p className="composer__hint">
        {disabledHint ?? t.chat.chatGeschlossen}
      </p>
    )
  }

  const wartet = fotos.filter((foto) => unterwegs(foto.stufe)).length

  /**
   * Der Sendeknopf -- **einmal geschrieben, an zwei Stellen gestellt.**
   *
   * Aus dem Betrieb (#63): „nach Auswahl der Fotos: der Sendeknopf ist nicht
   * in der Textzeile sondern außerhalb [...] so dass man sieht dass das Senden
   * die Fotos und den Text auslöst." Er steht deshalb mit Anhang oben an der
   * Kachelreihe und ohne Anhang wie bisher im Feld.
   *
   * **Zwei Fassungen davon wären zwei Wahrheiten.** An diesem Knopf hängt der
   * Ablauf aus #28 -- gesendet wird beim Aufsetzen des Fingers, nicht beim
   * Loslassen -- und der ist mühsam genug erarbeitet, um ihn nicht zu
   * verdoppeln. Verschieden ist allein die Klasse.
   */
  const sendeknopf = (klasse: string) => (
    <button
      type="button"
      className={klasse}
      disabled={!etwasZuSenden || zuLang || send.isPending || sendeWennFertig}
      /* **Gesendet wird beim Aufsetzen, nicht beim Loslassen.**

         Aus dem Betrieb (#28): „Senden-Knopf in der Eingabezeile: schließt
         zuerst die Tastatur, noch einmal drücken zum Senden." Der Ablauf
         dahinter: das Aufsetzen des Fingers gibt dem Knopf den Fokus, das Feld
         verliert ihn, die Tastatur fährt herunter -- und weil die Anwendung
         dabei wächst, wandert der Knopf unter dem Finger weg. Das `click`
         kommt nie an. Dieselbe Falle hat im Prüflauf zu #25 den Anmeldeknopf
         verfehlt.

         **Der erste Anlauf hat versucht, den Fokus zu halten**
         (`preventDefault` am Aufsetzen), und am iPad schien er zu greifen. Die
         Erklärung dafür war falsch und ist es wert, richtiggestellt zu werden:
         nicht weil dort keine Bildschirmtastatur im Spiel wäre -- es wurde mit
         beiden getestet --, sondern weil sie am iPad nach dem Senden **offen
         bleibt** und von Hand geschlossen wird. Bleibt sie stehen, wächst die
         Anwendung nicht, und der Knopf wandert nicht weg. Am iPhone fuhr sie
         herunter, und genau dort fiel es auf.

         Also hängt das Senden gar nicht mehr daran. Es geschieht beim
         Aufsetzen; was danach mit Fokus, Tastatur und Lage passiert, ist der
         Nachricht egal. `preventDefault` bleibt trotzdem stehen -- wo es
         greift, bleibt die Tastatur offen und man kann weiterschreiben.

         **`detail === 0` trennt die Tastatur vom Finger.** Ein `click` ohne
         Zeigerdruck kommt von der Eingabe- oder Leertaste auf dem fokussierten
         Knopf; nur der braucht hier noch einen Weg. Sonst würde jeder
         Fingerdruck zweimal senden. */
      onPointerDown={(event) => {
        notiere(`Knopf: Druck (${event.pointerType})`)
        event.preventDefault()
        submit()
      }}
      onClick={(event) => {
        notiere(`Knopf: Klick (detail ${event.detail})`)
        if (event.detail === 0) submit()
      }}
      onTouchStart={() => notiere('Knopf: Finger auf')}
      aria-label={t.chat.senden}
      title={t.chat.senden}
    >
      {/* **Ein echtes `<span>` und kein `::before`.** Die Trefferfläche bleibt
          44 Punkte, der sichtbare Kreis ist kleiner -- sonst füllte er die
          Pille bis an den Rand. Auf einem `<button>` ist eine Auflage in
          diesem Projekt ein Element, seit die GIF-Kacheln das gekostet
          haben. */}
      <span className="composer__kreis">
        <PfeilHochIcon size={19} />
      </span>
    </button>
  )

  /*
   * **Mit Anhang ist die Eingabezeile ein Block, ohne Anhang eine Zeile.**
   * Die Marke trägt beides: die Trennlinie zwischen Kacheln und Textzeile
   * faellt weg (aus zwei Streifen wird einer mit einer Kante nach oben), und
   * das Feld bekommt den Platz zurück, den der Knopf darin belegt hat.
   */
  const mitAnhang = fotos.length > 0 || problem !== null

  return (
    <div className={mitAnhang ? 'composer-area composer-area--anhang' : 'composer-area'}>
      {/* Worauf das hier antwortet -- direkt über dem Feld, in dem man
          schreibt. Weiter oben, bei der Sprechblase selbst, wäre es beim
          Tippen längst aus dem Bild. */}
      {bezug && (
        <div className="bezug">
          {bezugBild(bezug) && <img className="bezug__bild" src={bezugBild(bezug)!} alt="" />}
          <span className="bezug__text">
            <span className="bezug__autor">
              Antwort an {bezug.message.author.display_name}
              {bezug.mediaId !== undefined && ` · ${medienwort(bezugMedium(bezug)?.kind)}`}
            </span>
            <span className="bezug__auszug">
              {bezug.message.body !== '' ? bezug.message.body : medienwort(bezugMedium(bezug)?.kind)}
            </span>
          </span>
          <button
            type="button"
            className="icon-button bezug__weg"
            title={t.chat.bezugEntfernen}
            aria-label={t.chat.bezugEntfernen}
            onClick={() => onBezugWeg?.()}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      {/* **Erst kurz vor Schluss.** Ein Zähler, der von Zeichen eins an
          mitläuft, ist Aufsicht; einer, der bei neunzig Prozent erscheint,
          ist eine Warnung. Er steht über den Anhängen und damit über der
          Eingabezeile -- dort, wo auch die anderen Sätze zum Zustand stehen. */}
      <Restzeichen wert={text} grenze={grenzen.message} />

      {/* **Der Sendeknopf gehoert dem ganzen Block, nicht der Textzeile.**

          Aus dem Betrieb (#63): mit Anhang standen hier zwei getrennte
          Streifen uebereinander -- Kacheln oben, Textzeile unten, jede mit
          eigener Kante --, und der Knopf sass im Feld der unteren. Er sah
          damit aus, als gaelte er dem Text.

          **Der Knopf scrollt nicht mit.** Die Kachelreihe scrollt waagerecht;
          laege er darin, waere er bei sechs Fotos aus dem Bild. Er ist deshalb
          ein Geschwister der Reihe und kein Kind.

          **Waehrend einer Aufnahme steht er nicht da** -- dort gibt es nichts
          zu senden, was nicht erst beendet werden muesste. Das aendert sich
          mit dem neuen Aufnahmeablauf; heute ist es der Zustand von vorher. */}
      {mitAnhang && (
        <div className="anhangzeile">
          <div className="attachments">
            {fotos.map((foto) => (
              <Kachel key={foto.key} foto={foto} onRemove={() => remove(foto)} />
            ))}

            {problem && <span className="attachments__problem">{problem}</span>}
          </div>
          {!nimmtAuf && sendeknopf('anhangzeile__senden')}
        </div>
      )}

      {/* **Der Grund gehört hin, wo man ihn liest.** Er stand nur als
          `title` an der Kachel -- ein Tooltip, den auf einem Telefon nie
          jemand sieht. Aus der Prüfung dieser Runde: ein abgewiesenes Foto
          sah dort aus wie ein Fehlschlag ohne Erklärung. */}
      {fotos.some((f) => f.stufe === 'fehler' && f.problem) && (
        <p className="composer__status composer__status--fehler" role="alert">
          {[...new Set(fotos.filter((f) => f.stufe === 'fehler').map((f) => f.problem))]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}

      {/* **Was da rechnet, gehoert benannt.** Ein Balken ohne Wort sagt „warte",
          aber nicht worauf -- und Verkleinern ist das einzige, was hier lange
          dauern kann. */}
      {fotos.some((f) => f.stufe === 'rechnet' && f.art === 'video') && (
        <p className="composer__status" role="status">
          {t.chat.videoWirdVerkleinert}
        </p>
      )}

      {fotos.some((f) => f.hinweis) && (
        <p className="composer__status" role="status">
          {[...new Set(fotos.map((f) => f.hinweis))].filter(Boolean).join(' · ')}
        </p>
      )}

      {(wartet > 0 || sendeWennFertig) && (
        <p className="composer__status" role="status">
          {sendeWennFertig
            ? t.chat.sendetSpaeter
            : t.chat.fertigVon(fertige.length, fotos.length)}
        </p>
      )}

      {/* Ein Knopfdruck, auf den nichts folgt, ist das Schlimmste, was eine
          Eingabezeile tun kann -- man drückt noch einmal und noch einmal. Der
          Server schickt in jedem dieser Fälle einen brauchbaren Satz; er muss
          nur ankommen. */}
      {send.isError && !(send.error instanceof ApiError && send.error.offline) && (
        <p className="composer__status composer__status--fehler" role="alert">
          {send.error instanceof Error ? send.error.message : t.chat.nachrichtNichtRaus}
        </p>
      )}

      {/* **Nur noch im Rückfall.** Der Satz stand hier unter *jedem* Video --
          auch unter einem, das gerade von 33 MB auf 2,4 MB geschrumpft war,
          und widersprach damit der Zeile direkt darüber. Aus dem Betrieb, am
          Bildschirmfoto.

          Weg kommt er trotzdem nicht: kann ein Gerät nicht umrechnen, geht
          das Video wirklich unverändert hinaus, und dann ist „Am
          kompatibelsten" wieder der einzige Hebel, den der Absender hat. Der
          Grund steht davor (`hinweis`), der Rat dahinter. */}
      {fotos.some((f) => f.art === 'video' && f.hinweis) && (
        <p className="composer__status">
          {t.chat.kompatibelHinweis} <strong>{t.chat.kompatibelBetont}</strong>
          {t.chat.kompatibelRest}
        </p>
      )}

      {aktionStand !== null && (
        <p className="composer__status" role="status" data-aktion-stand="">
          {aktionStand}
        </p>
      )}

      {anhangOffen && (
        <Anhangwahl
          darfDateien={darfFotos}
          nurFotos={!istReisender}
          gifs={gifsAktiv}
          abstimmung={darfAbstimmung}
          wetter={darfWetter}
          wetterAnderswo={darfWetterAnderswo}
          tag={darfTag}
          onTag={() => {
            setAnhangOffen(false)
            void tagFragen()
          }}
          onAbstimmung={() => {
            setAnhangOffen(false)
            setAbstimmungOffen(true)
          }}
          onWetter={() => {
            setAnhangOffen(false)
            void wetterSchicken()
          }}
          onWetterAnderswo={() => {
            setAnhangOffen(false)
            setWetterwahlOffen(true)
          }}
          onFotoOderVideo={() => {
            // **Erst öffnen, dann schliessen.** Der Griff ans Dateifeld muss
            // in derselben Geste liegen; iOS lässt ihn sonst fallen.
            picker.current?.click()
            setAnhangOffen(false)
          }}
          onGif={() => {
            setAnhangOffen(false)
            setGifOffen(true)
          }}
          onSchliessen={() => setAnhangOffen(false)}
        />
      )}

      {gifOffen && (
        <Gifwahl tripId={tripId} onGewaehlt={nimmGif} onZu={() => setGifOffen(false)} />
      )}

      {wetterwahlOffen && (
        <Wetterwahl
          tripId={tripId}
          versatz={reiseVersatz}
          onWahl={(wahl) => void wetterAnderswoSchicken(wahl)}
          onAbbruch={() => setWetterwahlOffen(false)}
        />
      )}

      {tagwahl !== null && (
        <Tagwahl
          vorschau={tagwahl}
          versatz={reiseVersatz}
          holen={(day) => api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`, { day })}
          onWahl={(wahl) => void tagSchicken(wahl)}
          onAbbruch={() => setTagwahl(null)}
        />
      )}

      {abstimmungOffen && (
        <Abstimmungsformular tripId={tripId} onZu={() => setAbstimmungOffen(false)} />
      )}

      {/* **Die Reihe aus vier Knöpfen ist weg.**

          Seit die Eingabezeile ein `contenteditable` ist, bietet iOS von
          selbst *Format ▸ Fett · Kursiv · Unterstreichen* im eigenen
          Auswahlmenü an — dafür braucht es hier nichts mehr. Aus dem
          Betrieb: „die Knopfsymbole wegnehmen, die benötigen viel
          Anzeigeplatz."

          **Durchgestrichen und Kapitälchen kennt Apples Menü nicht**, und
          eine Webseite kann dort nichts hinzufügen — das kann nur eine native
          App. Die beiden stehen deshalb in der Eingabezeile selbst (unten),
          und zwar nur, solange etwas markiert ist: keine eigene Reihe, kein
          Platz im Ruhezustand. */}

      {/* Was das Mikrofon zu sagen hat, steht als Text da — nicht als
          Tooltip, den auf dem Telefon niemand sieht. */}
      {tonHinweis && (
        <p className="composer__status composer__status--fehler" role="alert">
          {tonHinweis}
        </p>
      )}

      {/* **Beim Aufnehmen verschwindet die Eingabezeile ganz.**

          Aus dem Betrieb (#63): „die Eingabezeile verschwindet ganz,
          stattdessen kann der Platz für die Anzeige der Symbole (Pause)
          genutzt werden." Vorher stand die Aufnahme *neben* dem Feld: zwei
          kleine Knöpfe im Feld, und daneben ein Textfeld, das währenddessen
          nichts zu tun hatte -- man konnte hineintippen, während das Mikrofon
          lief. */}
      {nimmtAuf ? (
        <Aufnahmeleiste
          dauerMs={seit}
          pausiert={pausiert}
          kannPausieren={aufnahme.current?.kannPausieren ?? false}
          pegel={() => aufnahme.current?.pegel() ?? 0}
          onVerwerfen={brichAufnahmeAb}
          onPause={pausiereAufnahme}
          onWeiter={weiterAufnehmen}
          onSenden={() => void beendeAufnahme(true)}
        />
      ) : (
      <div className="composer">
        {/* **Die Dateiauswahl steht seit #59 allen offen** -- Mitlesende
            wählen Fotos, der Reisende dazu Videos. Vorher stand hier für alle
            anderen ein Dialog, der Fotos anbot und Fotos ablehnte. */}
        {darfFotos && (
          <>
            <input
              ref={picker}
              type="file"
              /* **`accept` ist keine Sperre** (steht so in CLAUDE.md), sondern
                 ein Vorschlag an die Dateiauswahl. Was ein Mitlesender
                 trotzdem auswählt, weist `add()` unten mit einem Satz ab --
                 und der Server ohnehin. */
              accept={istReisender ? 'image/*,video/*' : 'image/*'}
              multiple
              hidden
              onChange={(event) => {
                void add(event.target.files)
                // Zurücksetzen, sonst löst dieselbe Datei kein zweites Mal aus.
                event.target.value = ''
              }}
            />
            {/* **Hier stand ein zweites Dateifeld mit `capture="environment"`**
                -- der eigene Weg zur Kamera. Er ist heraus, auf Ansage aus
                dem Betrieb: „die Kamera aus dem Auswahlmenü herausnehmen (nur
                Foto/Video + GIF)."

                Verloren geht dabei nichts: die Foto-App eines Telefons hat
                einen Auslöser, und das Feld darüber führt dorthin. Gewonnen
                ist ein Weg weniger, der eigene Sonderfälle mitbrachte --
                `capture` ist der Weg, über den die gemeldeten 360p ins Haus
                kamen. */}
          </>
        )}

        {/* **Ein Knopf statt zweier.**

            Aus dem Betrieb: „am Smartphone ist das Eingabefeld zu schmal [...]
            oder durch ein + ersetzen, und dann ein Auswahlfenster mit
            Foto/Video / Kamera / GIF." Die Kamera ist auf spätere Ansage
            wieder heraus (siehe das Dateifeld darüber); geblieben ist der
            eine Knopf.

            Jeder Knopf hier ist 44 Punkte breit, weil er es sein muss; was die
            Knöpfe nehmen, fehlt dem Text. Zwei in einen zu legen gibt dem Feld
            52 Punkte zurück.

            **Bei nur einem Weg bleibt der Knopf, der er war.** Ein `+`, das
            ein Blatt mit einer einzigen Zeile öffnet, wäre ein Griff mehr für
            nichts -- und genau das ist die Lage der Mitlesenden, die GIFs
            schicken dürfen und sonst nichts. */}
        {wege >= 2 ? (
          <button
            type="button"
            className={
              anhangOffen
                ? 'composer__attach composer__attach--rund composer__attach--an'
                : 'composer__attach composer__attach--rund'
            }
            onClick={() => {
              setAnhangOffen((offen) => !offen)
              setGifOffen(false)
            }}
            aria-label={t.chat.etwasAnhaengen}
            aria-expanded={anhangOffen}
            title={t.chat.etwasAnhaengen}
          >
            <PlusIcon size={22} />
          </button>
        ) : (
          <>
            {darfFotos && (
              <button
                type="button"
                className="composer__attach composer__attach--rund"
                onClick={() => picker.current?.click()}
                aria-label={
                  istReisender ? t.chat.fotosUndVideosHinzufuegen : t.chat.fotosHinzufuegen
                }
                title={istReisender ? t.chat.fotosUndVideosHinzufuegen : t.chat.fotosHinzufuegen}
              >
                <PhotoIcon size={20} />
              </button>
            )}
            {gifsAktiv && (
              <button
                type="button"
                className={
                  gifOffen
                    ? 'composer__attach composer__attach--rund composer__attach--an'
                    : 'composer__attach composer__attach--rund'
                }
                onClick={() => setGifOffen((offen) => !offen)}
                aria-label={t.chat.gifWahl}
                aria-expanded={gifOffen}
                title={t.chat.gifWahl}
              >
                <GifIcon size={20} />
              </button>
            )}
          </>
        )}

        {/* **Was Apples Menü nicht kann — und nur, solange markiert ist.**
            Fett und Kursiv holt man sich dort; diese beiden gibt es nicht,
            also stehen sie hier. Sie erscheinen mit der Markierung und
            verschwinden mit ihr, kosten also im Ruhezustand keinen Platz. */}
        {hatAuswahl &&
          AUSZEICHNUNGEN.filter((a) => a.art === 'durch' || a.art === 'kapitael').map((a) => (
            <button
              key={a.zeichen}
              type="button"
              className={`composer__attach auszeichnen__knopf--${a.art}`}
              title={a.name}
              aria-label={a.name}
              // Ein Knopf nimmt beim Antippen den Fokus, und mit dem Fokus
              // geht die Markierung -- danach wäre nichts mehr auszuzeichnen.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => lege(a.art)}
            >
              {a.art === 'durch' ? 'D' : 'Kap'}
            </button>
          ))}

        {/* **Ein `contenteditable`, kein `textarea`.** Ein Textfeld kann eine
            Schrift haben, nicht zwei — fett sähe darin aus wie mager, und die
            Sternchen stünden weiter da. Warum der Trick mit einer Fassung
            dahinter nicht geht, steht in `reichtext.ts`.

            **Ungesteuert, mit Absicht.** Der Zustand wird aus dem Feld
            gelesen, nie hineingeschrieben; ein `value`, das React bei jedem
            Tastendruck zurückschreibt, setzte den Cursor ans Ende. Geschrieben
            wird nur, wenn die Anwendung es meint: nach dem Senden. */}
        {/* **Die Fassung um das Feld — und der Knopf liegt darin** (#8, #22).
            Aus dem Betrieb: „das Eingabefeld ist noch zu klein", daneben ein
            Bildschirmfoto von iMessage. Dort steht rechts vom Feld **kein**
            Knopf, sondern einer *im* Feld.

            Gemessen auf 390 Punkten: vorher blieben dem Text 182 Punkte
            (Feld 210 minus Innenabstand), jetzt sind es 254. Zwei
            44-Punkt-Knöpfe und ihre Abstände sind genau der Unterschied.

            **Der Knopf ist ein Geschwister des Feldes, kein Kind.** Ein
            `<button>` in einem `contenteditable` ist bearbeitbarer Inhalt:
            `lese()` schriebe ihn mit, und der Cursor könnte darin landen. */}
        <div className="composer__feld">
          <div
            ref={field}
            className="composer__input"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            /* **Kein `enterKeyHint="send"` mehr.** Hier stand einmal die
               Begründung, die Taste heiße „Senden", weil sie das auch tut. Seit
               #28 tut sie es auf der Bildschirmtastatur *nicht* mehr -- dort
               macht sie den Absatz, wie in iMessage und WhatsApp. Eine blaue
               Sendetaste, die einen Absatz macht, wäre die Falle, die der alte
               Satz vermeiden wollte, nur andersherum. */
            aria-label={platzhalter[0]}
            onInput={() => {
              setText(lese(field.current as HTMLElement))
              grow()
            }}
            onPaste={(event) => {
              // Aus einer fremden Seite kommt fremdes HTML. Hier zählt der
              // Wortlaut, und sonst nichts.
              event.preventDefault()
              alsText(event.clipboardData.getData('text/plain'))
              setText(lese(field.current as HTMLElement))
              grow()
            }}
            onKeyDown={(event) => {
              // **Die Eingabetaste sendet, überall.**
              //
              // Aus dem Betrieb: „Senden (wenn der Sendeknopf da ist): mit
              // ENTER." Hier stand vorher eine Abfrage auf `pointer: coarse`,
              // und die hat den Fall verfehlt, aus dem die Meldung kam: **ein
              // iPad mit angeschlossener Tastatur meldet einen groben
              // Zeiger.** Dort tippte also eine echte Tastatur, und die
              // Eingabetaste tat nichts. Ein Merkmal des *Bildschirms* kann
              // nicht beantworten, womit gerade geschrieben wird.
              //
              // Umschalt+Eingabe macht den Absatz, wie im Feld zum Ändern
              // einer Nachricht. Der Preis gehört genannt: am Telefon ist ein
              // Absatz damit umständlicher als vorher.
              //
              // **Ob es etwas zu senden gibt, entscheidet `submit()`** — und
              // sonst niemand. Hier stand zuerst `etwasZuSenden` davor, damit
              // Taste und Knopf dieselbe Bedingung tragen; die Gegenprobe hat
              // gezeigt, dass sie gar nichts entscheidet, weil `submit()` den
              // leeren Fall ohnehin abfängt. Eine dritte Kopie derselben Regel
              // ist die Stelle, an der sie auseinanderläuft.
              if (event.key === 'Enter' && !event.shiftKey) eingabetaste(event)
            }}
          />

          {/* **Der Platzhalter ist eine Auflage und kein `::before` mehr.**
              Aus dem Betrieb: die Hinweiszeile über der Zeile sollte weg und
              durch ein Sinnbild im leeren Feld ersetzt werden. In
              `content: attr(...)` kann kein SVG stehen.

              **Und kein Spiel mit dem Innenabstand.** Das Feld beim ersten
              Zeichen von `14 + Sinnbild` auf `14` zurückzusetzen ließe den
              Text sichtbar springen. Die Auflage liegt darüber; der Cursor
              steht die ganze Zeit an derselben Stelle.

              `aria-hidden`: derselbe Wortlaut steht als `aria-label` am Feld,
              und zweimal vorgelesen ist einmal zu viel. */}
          {text === '' && !nimmtAuf && (
            <span className="composer__leer" aria-hidden="true">
              {reichweite === 'alle' ? <GruppeIcon size={16} /> : <PersonIcon size={16} />}
              <span className="composer__leer-text">
                {platzhalter[stufe] ?? platzhalter[0]}
              </span>
            </span>
          )}

          {/* Während der Aufnahme liegt der Zähler über dem Feld statt daneben
              -- geschrieben wird dabei ohnehin nicht, und der Platzhalter
              darunter ist abgeschaltet. */}
          {/* **Genau einer, und er wird abgeleitet.** Vorher standen hier zwei
              Bedingungen mit derselben Aussage (`!text.trim()` am Mikrofon,
              `!text.trim() && fotos.length === 0` am Senden) -- die erste
              kannte die Fotos nicht: mit einem angehängten Bild und ohne Text
              zeigte die Zeile Mikrofon **und** Senden.

              Der letzte Fall deckt zugleich den Browser ohne Aufnahme ab: dort
              steht der Sendeknopf wie bisher da und ist ausgegraut. Eine leere
              rechte Seite sähe kaputt aus.

              **Mit Anhang bleibt sie trotzdem leer, und das ist kein Bruch
              mit dieser Begründung** (#63): die galt der *leeren* Zeile, in
              der es sonst gar keinen Knopf gäbe. Hängt etwas, steht der
              Sendeknopf oben an der Kachelreihe -- auf Augenhöhe mit dem, was
              er verschickt --, und das Feld bekommt den Platz zurück, den er
              darin belegt hat.

              **Und bei offener Bildschirmtastatur ebenso.** Aus dem Betrieb,
              nachdem das `+` daneben beiseitegetreten war: „das gleiche gilt
              auch für den Knopf der Sprachnachricht." Er hat dieselbe
              Schwäche -- ein Tipp nimmt dem Feld den Fokus, iOS fährt die
              Tastatur herunter, die Zeile wandert nach unten, und das
              Loslassen trifft ihn nicht mehr.

              **Hier reicht das Ausblenden per CSS aber nicht**, anders als
              beim `+`: an dieser Stelle steht *ein* Knopf, und nähme man ihn
              weg, wäre die rechte Seite leer. Also tritt der Sendeknopf an
              seine Stelle -- derselbe Zweig, der auch den Browser ohne
              Aufnahme trägt. Wer schreiben will, hat die Tastatur ohnehin
              offen; wer sprechen will, tippt neben das Feld. */}
          <span className="composer__innen">
            {!etwasZuSenden && kannAufnehmen() && !tastaturOffen ? (
              <button
                type="button"
                className="composer__innen-knopf"
                onClick={() => void nimmAuf()}
                aria-label={t.chat.aufnahme}
                title={t.chat.aufnahme}
              >
                <WelleIcon size={20} />
              </button>
            ) : mitAnhang ? null : (
              sendeknopf('composer__innen-knopf')
            )}
          </span>
        </div>
      </div>
      )}
    </div>
  )
}

/**
 * Eine Kachel je Foto -- mit dem Stand der Dinge darauf.
 *
 * Vorbereiten und Hochladen sind zwei verschiedene Wartezeiten: das eine
 * hängt am Telefon, das andere am Netz. Wer unterwegs steht und sich fragt,
 * ob es am Empfang liegt, sieht es hier.
 */
/** Ob an dieser Kachel gerade gearbeitet wird -- Warten auf Netz zählt nicht. */
function unterwegs(stufe: Stufe): boolean {
  return stufe === 'wartet' || stufe === 'rechnet' || stufe === 'lädt'
}

function Kachel({ foto, onRemove }: { foto: Attachment; onRemove: () => void }) {
  const t = useTexte()
  const laeuft = unterwegs(foto.stufe)

  return (
    <div className="attachment-item">
    <div
      className={foto.stufe === 'fehler' ? 'attachment attachment--fehler' : 'attachment'}
      title={foto.problem ?? undefined}
    >
      {foto.preview ? (
        <img src={foto.preview} alt="" className={laeuft ? 'attachment__blass' : undefined} />
      ) : (
        <span className="attachment__leer">
          {/* Ohne Standbild soll wenigstens dastehen, um was es sich
              handelt -- ein Bildsymbol an einem Video führt in die Irre. */}
          {foto.art === 'video' ? (
            <VideoIcon size={18} />
          ) : foto.art === 'ton' ? (
            <WelleIcon size={18} />
          ) : (
            <PhotoIcon size={18} />
          )}
        </span>
      )}

      {laeuft && (
        <span className="attachment__stufe">
          {/* **Auch beim Rechnen eine Zahl.** Hier stand `…` fuer alles ausser
              dem Hochladen -- bei einem Foto ist das ein Wimpernschlag, beim
              Verkleinern eines Videos steht man eine Minute davor und weiss
              nicht, ob noch etwas passiert. Die Punkte bleiben fuer den
              Moment, in dem es noch keine Zahl gibt. */}
          {foto.fortschritt > 0 && foto.stufe !== 'wartet'
            ? `${Math.round(foto.fortschritt * 100)} %`
            : '…'}
        </span>
      )}

      {/* Fertig gerechnet, aber ohne Netz: die Kachel sagt, dass sie wartet --
          sonst sähe sie aus wie eine, die durch ist. */}
      {foto.stufe === 'offline' && (
        <span className="attachment__stufe" title={t.chat.wartetAufNetz}>
          ⏳
        </span>
      )}

      {foto.stufe === 'lädt' && (
        <span className="attachment__balken">
          <span style={{ width: `${Math.round(foto.fortschritt * 100)}%` }} />
        </span>
      )}

      {foto.stufe === 'fehler' && <span className="attachment__stufe">!</span>}

      {/* Solange es läuft, gibt es nichts wegzunehmen -- der Upload wäre
          ohnehin schon unterwegs. */}
      {!laeuft && (
        <button
          type="button"
          className="attachment__remove"
          onClick={onRemove}
          aria-label={t.chat.fotoEntfernen}
          title={t.chat.entfernen}
        >
          <XIcon size={13} />
        </button>
      )}
    </div>

    {/* Was da rausgeht, in Zahlen. Bei einem verkleinerten Foto ist es eine
        Beruhigung, bei einem Video aus dem Teilen-Dialog manchmal eine
        Warnung -- und beides sieht man besser vorher als hinterher. */}
    <span
      className={
        foto.ueberGrenze ? 'attachment__groesse attachment__groesse--zuviel' : 'attachment__groesse'
      }
      title={foto.ueberGrenze ? t.chat.grenzeLiegtBei(foto.ueberGrenze) : undefined}
    >
      {/* **Die Teile werden gesammelt und dann verbunden**, statt jeder für
          sich einen Punkt mitzubringen. Vorher trug jedes Stück sein eigenes
          „ · " vor sich her, und sobald das erste wegfiel -- was seit der
          Ausbeute der Regelfall ist --, fing die Zeile mit einem Trennpunkt
          an: „· 13 kB · 360p · H.264". */}
      {[
        // Wo eine Ausbeute steht, steht die Grösse dort schon -- „2,4 MB ·
        // 0:18 · 33 MB → 2,4 MB" sagt dieselbe Zahl zweimal.
        foto.ausbeute ? '' : groesse(foto.bytes),
        (foto.art === 'video' || foto.art === 'ton')
        && foto.dauerMs !== undefined
        && foto.dauerMs > 0
          ? dauerText(foto.dauerMs)
          : '',
        // Nicht erst am Ende des Uploads: die Zahl steht ohnehin da, und
        // daneben passt der Grund, warum sie zu hoch ist.
        foto.ueberGrenze ? t.chat.ueberGrenze(foto.ueberGrenze) : '',
        // Was das Verkleinern gebracht hat, samt Auflösung und Format -- die
        // Angaben, nach denen im Betrieb gefragt wurde.
        foto.ausbeute ?? '',
      ]
        .filter((t) => t !== '')
        .join(' · ')}
    </span>

    {/* **Der Grund steht als Text da, nicht nur als `title`** (#41).
        Ein Tooltip ist auf dem Telefon unsichtbar -- das ist die Regel aus der
        Codereview, und hier wog sie neuerdings schwerer: seit ein Video, das
        sich nicht in H.264 bringen lässt, gar nicht mehr hochgeht, ist dieser
        Satz das Einzige, was den Unterschied zwischen „geht nicht" und „tut
        nichts" ausmacht. Am Kasten steht er weiterhin auch als `title` -- für
        die Maus, wo ein langer Satz sonst die Kachel breit machen würde. */}
    {foto.problem !== undefined && foto.problem !== null && (
      <span className="attachment__grund" role="alert">{foto.problem}</span>
    )}
    </div>
  )
}


/*
 * **Hier stand `isTouch()`** -- „grober Zeiger, also ein Finger, also
 * wahrscheinlich eine Kamera". Der einzige verbliebene Aufrufer war die
 * Kamerazeile im Auswahlmenü, und die ist heraus.
 *
 * Die Erfahrung dahinter bleibt aufgehoben, denn sie war teuer: das Merkmal
 * stand einmal an der Eingabetaste und hat dort den Fall verfehlt, aus dem
 * die Meldung kam -- ein iPad mit angeschlossener Tastatur meldet einen
 * groben Zeiger. **Ein Merkmal des Bildschirms kann nicht beantworten, womit
 * gerade geschrieben wird.**
 */
